import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, Grid, TextField, Tooltip, Typography } from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Bold,
  Essentials,
  Heading,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  Paragraph,
  Table,
  Undo,
  FontFamily,
  ImageBlock,
  ImageCaption,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { cssProperties } from "../../utils/commonCssProperties";
import { createTemplateApi, getSystemVariablesApi, getTemplateApi, updateTemplateApi, errorMessage } from "./api";
import { labelSx, useFeedback } from "./common";
import { CHANNEL_LABELS, EMAIL_PLACEHOLDER, keyOf, slotCount } from "./variables";
import VariablesEditor, { checkVariables, editableVariables, variablesPayload } from "./VariablesEditor";
import PreviewDialog from "./PreviewDialog";

const EDITOR_CONFIG = {
  toolbar: ["undo", "redo", "|", "heading", "fontFamily", "|", "bold", "italic", "|", "link", "insertImageViaUrl", "insertTable", "|", "bulletedList", "numberedList", "indent", "outdent"],
  plugins: [Bold, Essentials, Heading, Indent, IndentBlock, Italic, Link, List, Paragraph, Table, Undo, FontFamily,
    ImageBlock, ImageCaption, ImageInsertViaUrl, ImageResize, ImageStyle, ImageTextAlternative, ImageToolbar],
  // Images are inserted by link (https://...), one per line; the service turns size / alignment into email styles.
  image: {
    toolbar: ["imageTextAlternative", "toggleImageCaption", "|", "imageStyle:alignBlockLeft", "imageStyle:block", "imageStyle:alignBlockRight", "|", "resizeImage"],
    resizeOptions: [
      { name: "resizeImage:original", value: null, label: "Original" },
      { name: "resizeImage:25", value: "25", label: "25%" },
      { name: "resizeImage:50", value: "50", label: "50%" },
      { name: "resizeImage:75", value: "75", label: "75%" },
    ],
  },
};

const sectionSx = { border: "1px solid #e3e5f5", borderRadius: "8px", p: 2, backgroundColor: "#fafbff" };
const emptySms = { dltid: "", text: "", slots: [] };
const emptyWhatsapp = { templatename: "", language: "en", text: "", slots: [] };
const emptyEmail = { subject: "", body: "", textbody: "" };

// The value picker for each {#var#} slot of a text.
function SlotMapping({ text, slots, onChange, options, errors, prefix }) {
  const count = slotCount(text);
  if (!count) return <Typography variant="caption" sx={{ color: "#666" }}>No {"{#var#}"} in the text yet.</Typography>;
  const list = Array.from({ length: count }, (_, i) => slots[i] || "");
  return (
    <Grid container spacing={1}>
      {list.map((value, i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Autocomplete
            freeSolo
            size="small"
            options={options}
            value={value}
            onInputChange={(e, v) => onChange(list.map((x, j) => (j === i ? v : x)))}
            renderInput={(props) => <TextField {...props} label={`{#var#} ${i + 1}`} error={!!errors[`${prefix}${i}`]} helperText={errors[`${prefix}${i}`] || ""} />}
          />
        </Grid>
      ))}
    </Grid>
  );
}

// Create / edit a notification template: one event with SMS, WhatsApp and Email parts.
export default function TemplateForm() {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const [searchParams] = useSearchParams();
  const project = Cookies.get("mhet_mst_project");
  const templateid = searchParams.get("id");
  const isEdit = !!templateid;

  const [loaded, setLoaded] = useState(!isEdit);
  const [version, setVersion] = useState(null);
  const [code, setCode] = useState("");
  const [eventname, setEventname] = useState("");
  const [description, setDescription] = useState("");
  const [variables, setVariables] = useState([]);
  const [on, setOn] = useState({ sms: false, whatsapp: false, email: true });
  const [sms, setSms] = useState(emptySms);
  const [whatsapp, setWhatsapp] = useState(emptyWhatsapp);
  const [email, setEmail] = useState(emptyEmail);
  const [ownText, setOwnText] = useState(false);
  const [initialHtml, setInitialHtml] = useState(""); // given to the editor once
  const [systemVariables, setSystemVariables] = useState([]);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const editorRef = useRef(null);
  const subjectRef = useRef(null);
  const lastEmailField = useRef("body");

  useEffect(() => {
    (async () => {
      let system = [];
      try {
        system = (await getSystemVariablesApi()) || [];
        setSystemVariables(system);
      } catch (err) {
        feedback.failed(errorMessage(err, "Could not load the database values"));
      }
      if (!isEdit) return;
      feedback.loader(true);
      try {
        const t = await getTemplateApi(templateid);
        const labelOf = (key) => (t.variables || []).find((v) => v.name === key)?.label || system.find((s) => s.name === key)?.label || key;
        setCode(t.code);
        setEventname(t.eventname);
        setDescription(t.description || "");
        setVariables(editableVariables(t.variables));
        setOn({ sms: !!t.sms, whatsapp: !!t.whatsapp, email: !!t.email });
        if (t.sms) setSms({ ...t.sms, slots: t.sms.slots.map(labelOf) });
        if (t.whatsapp) setWhatsapp({ ...t.whatsapp, slots: t.whatsapp.slots.map(labelOf) });
        if (t.email) {
          setEmail({ ...emptyEmail, ...t.email, textbody: t.email.textbody || "" });
          setInitialHtml(t.email.body);
          setOwnText(!!t.email.textbody);
        }
        setVersion(t.version);
        setLoaded(true);
      } catch (err) {
        feedback.failed(errorMessage(err, "Could not load the template"));
      }
      feedback.loader(false);
    })();
    // eslint-disable-next-line
  }, [templateid]);

  // Every value the texts use: { key, label } (labels as written).
  const used = useMemo(() => {
    const list = [];
    if (on.email) {
      [email.subject, email.body, ownText ? email.textbody : ""].forEach((t) => {
        for (const m of String(t || "").matchAll(EMAIL_PLACEHOLDER)) list.push({ key: keyOf(m[1]), label: m[1] });
      });
    }
    if (on.sms) sms.slots.slice(0, slotCount(sms.text)).forEach((l) => l && list.push({ key: keyOf(l), label: l }));
    if (on.whatsapp) whatsapp.slots.slice(0, slotCount(whatsapp.text)).forEach((l) => l && list.push({ key: keyOf(l), label: l }));
    return list;
  }, [on, email, ownText, sms, whatsapp]);

  const valueLabels = [...new Set([...systemVariables.map((s) => s.label), ...variables.map((v) => v.label.trim()).filter(Boolean)])];

  // Puts {Label} where the cursor is, in the email subject or body (whichever was used last).
  const insertValue = (label) => {
    const text = `{${label}}`;
    if (lastEmailField.current === "subject" && subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart ?? email.subject.length;
      const end = el.selectionEnd ?? email.subject.length;
      setEmail({ ...email, subject: email.subject.slice(0, start) + text + email.subject.slice(end) });
      setTimeout(() => { el.focus(); el.setSelectionRange(start + text.length, start + text.length); }, 0);
      return;
    }
    const editor = editorRef.current;
    if (editor) {
      editor.model.change((writer) => editor.model.insertContent(writer.createText(text)));
      editor.editing.view.focus();
    }
  };

  const validate = () => {
    const e = {};
    if (!isEdit && !/^[A-Za-z][A-Za-z0-9_]{1,49}$/.test(code.trim())) e.code = "2-50 letters, digits or _, starting with a letter";
    if (!eventname.trim()) e.eventname = "Event name is required";
    if (!on.sms && !on.whatsapp && !on.email) e.channels = "Turn on at least one of SMS, WhatsApp, Email";
    if (on.sms) {
      if (!/^\d{10,25}$/.test(sms.dltid.trim())) e.smsdltid = "The DLT template id (digits)";
      if (!sms.text.trim()) e.smstext = "The DLT-approved text is required";
      sms.slots.slice(0, slotCount(sms.text)).forEach((s, i) => { if (!String(s || "").trim()) e[`sms${i}`] = "Choose a value"; });
      for (let i = sms.slots.length; i < slotCount(sms.text); i++) e[`sms${i}`] = "Choose a value";
    }
    if (on.whatsapp) {
      if (!/^[a-z0-9_]{1,200}$/.test(whatsapp.templatename.trim())) e.watemplatename = "The approved template name (lower-case, digits, _)";
      if (!whatsapp.text.trim()) e.watext = "The approved text is required";
      for (let i = 0; i < slotCount(whatsapp.text); i++) if (!String(whatsapp.slots[i] || "").trim()) e[`wa${i}`] = "Choose a value";
    }
    if (on.email) {
      if (!email.subject.trim()) e.subject = "Subject is required";
      if (!email.body || email.body.replace(/<[^>]+>/g, "").trim() === "") e.body = "Body is required";
    }
    const check = checkVariables(variables, systemVariables, used);
    Object.assign(e, check.errors);
    if (check.undeclared.length) e.undeclared = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const trimSlots = (part) => ({ ...part, slots: part.slots.slice(0, slotCount(part.text)).map((s) => String(s).trim()) });
  const payload = () => ({
    ...(isEdit ? {} : { code: code.trim().toUpperCase() }),
    eventname: eventname.trim(),
    description: description.trim(),
    variables: variablesPayload(variables),
    sms: on.sms ? trimSlots({ dltid: sms.dltid.trim(), text: sms.text, slots: sms.slots }) : null,
    whatsapp: on.whatsapp ? trimSlots({ templatename: whatsapp.templatename.trim(), language: whatsapp.language.trim() || "en", text: whatsapp.text, slots: whatsapp.slots }) : null,
    email: on.email ? { subject: email.subject.trim(), body: email.body, textbody: ownText ? email.textbody : "" } : null,
  });

  const save = async () => {
    setServerError("");
    if (!validate()) return;
    setSaving(true);
    try {
      const res = isEdit ? await updateTemplateApi(templateid, payload()) : await createTemplateApi(payload());
      feedback.success(res.message || "Template saved");
      navigate(`/${project}/notification-templates?page=list`);
    } catch (err) {
      setServerError(errorMessage(err, "Could not save the template"));
    }
    setSaving(false);
  };

  if (!loaded) return null;

  return (
    <Box sx={{ maxWidth: 1150 }}>
      <Grid container spacing={2}>
        {isEdit && (
          <Grid item xs={12}>
            <Chip label={`${code} · version ${version}`} sx={{ fontWeight: 600 }} />
            <Typography variant="caption" sx={{ ml: 1, color: "#666" }}>Saving creates version {version + 1}; messages already sent keep their version.</Typography>
          </Grid>
        )}
        {!isEdit && (
          <Grid item xs={12} md={3}>
            <Typography sx={labelSx}>Code *</Typography>
            <TextField fullWidth size="small" value={code} placeholder="e.g. HALL_TICKET" onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, "_"))} error={!!errors.code} helperText={errors.code || "Short unique name"} />
          </Grid>
        )}
        <Grid item xs={12} md={isEdit ? 6 : 5}>
          <Typography sx={labelSx}>Event *</Typography>
          <TextField fullWidth size="small" value={eventname} placeholder="e.g. Hall Ticket - 1st admit card" onChange={(e) => setEventname(e.target.value)} error={!!errors.eventname} helperText={errors.eventname} />
        </Grid>
        <Grid item xs={12} md={isEdit ? 6 : 4}>
          <Typography sx={labelSx}>Description</Typography>
          <TextField fullWidth size="small" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Grid>

        <Grid item xs={12}>
          <Typography sx={labelSx}>Channels</Typography>
          {["sms", "whatsapp", "email"].map((c) => (
            <FormControlLabel key={c} control={<Checkbox checked={on[c]} onChange={(e) => setOn({ ...on, [c]: e.target.checked })} />} label={CHANNEL_LABELS[c]} />
          ))}
          {errors.channels && <Typography variant="caption" sx={{ color: "red" }}>{errors.channels}</Typography>}
        </Grid>

        <Grid item xs={12}>
          <VariablesEditor
            variables={variables}
            onChange={setVariables}
            systemVariables={systemVariables}
            used={used}
            errors={errors}
            showUndeclaredError={!!errors.undeclared}
            help={<>Values from the database ({systemVariables.map((s) => s.label).join(", ")}) can be used directly. Declare every other value: <b>Typed when sending</b> (same for everyone, e.g. Start Date) or <b>Column in the upload file</b> (email uploads, e.g. Roll No — the file needs a column with that label).</>}
          />
        </Grid>

        {on.sms && (
          <Grid item xs={12}>
            <Box sx={sectionSx}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>SMS</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography sx={labelSx}>DLT Template ID *</Typography>
                  <TextField fullWidth size="small" value={sms.dltid} onChange={(e) => setSms({ ...sms, dltid: e.target.value.trim() })} error={!!errors.smsdltid} helperText={errors.smsdltid || "As registered on the DLT portal"} />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={labelSx}>SMS Text *</Typography>
                  <TextField fullWidth multiline minRows={3} size="small" value={sms.text} onChange={(e) => setSms({ ...sms, text: e.target.value })} error={!!errors.smstext}
                    helperText={errors.smstext || `Paste the DLT-approved text exactly, with {#var#}. ${slotCount(sms.text)} value slot(s) · ${sms.text.length} characters`}
                    placeholder="Dear Candidate, hall ticket for {#var#}{#var#} is now available for download, please visit {#var#}{#var#}. CET CELL, Mumbai" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Value for each {"{#var#}"} (next to each other, {"{#var#}{#var#}"} is one value):</Typography>
                  <SlotMapping text={sms.text} slots={sms.slots} onChange={(slots) => setSms({ ...sms, slots })} options={valueLabels} errors={errors} prefix="sms" />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}

        {on.whatsapp && (
          <Grid item xs={12}>
            <Box sx={sectionSx}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>WhatsApp</Typography>
                {on.sms && <Button size="small" onClick={() => setWhatsapp({ ...whatsapp, text: sms.text, slots: [...sms.slots] })}>Copy text and values from SMS</Button>}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Typography sx={labelSx}>Approved Template Name *</Typography>
                  <TextField fullWidth size="small" value={whatsapp.templatename} onChange={(e) => setWhatsapp({ ...whatsapp, templatename: e.target.value.trim().toLowerCase() })} error={!!errors.watemplatename} helperText={errors.watemplatename || "Exactly as approved, e.g. v2_pgp_template_1__clone"} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography sx={labelSx}>Language</Typography>
                  <TextField fullWidth size="small" value={whatsapp.language} onChange={(e) => setWhatsapp({ ...whatsapp, language: e.target.value.trim() })} helperText="e.g. en" />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={labelSx}>WhatsApp Text *</Typography>
                  <TextField fullWidth multiline minRows={3} size="small" value={whatsapp.text} onChange={(e) => setWhatsapp({ ...whatsapp, text: e.target.value })} error={!!errors.watext}
                    helperText={errors.watext || `The approved text with {#var#}; the values are sent in order as {{1}}, {{2}}... ${slotCount(whatsapp.text)} value slot(s)`} />
                </Grid>
                <Grid item xs={12}>
                  <SlotMapping text={whatsapp.text} slots={whatsapp.slots} onChange={(slots) => setWhatsapp({ ...whatsapp, slots })} options={valueLabels} errors={errors} prefix="wa" />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}

        {on.email && (
          <Grid item xs={12}>
            <Box sx={sectionSx}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Email</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Insert a value:</Typography>
                {valueLabels.map((l) => {
                  const sys = systemVariables.find((s) => s.label === l);
                  return (
                    <Tooltip key={l} title={sys ? `From database: ${sys.description}` : "Your variable"}>
                      <Chip size="small" label={`{${l}}`} onClick={() => insertValue(l)} sx={{ backgroundColor: sys ? "rgba(63,65,209,0.10)" : "rgba(230,151,171,0.25)" }} />
                    </Tooltip>
                  );
                })}
              </Box>
              <Typography sx={labelSx}>Subject *</Typography>
              <TextField fullWidth size="small" inputRef={subjectRef} value={email.subject} onFocus={() => { lastEmailField.current = "subject"; }}
                onChange={(e) => setEmail({ ...email, subject: e.target.value.replace(/[\r\n]/g, " ") })} error={!!errors.subject} helperText={errors.subject} placeholder="e.g. {Course Name} - Download Hall Ticket/Admit Card" />
              <Typography sx={{ ...labelSx, mt: 2 }}>Body *</Typography>
              <Box onFocusCapture={() => { lastEmailField.current = "body"; }} sx={{ "& .ck-editor__editable": { minHeight: 240 } }}>
                <CKEditor editor={ClassicEditor} config={EDITOR_CONFIG} data={initialHtml} onReady={(editor) => { editorRef.current = editor; }} onChange={(event, editor) => setEmail((m) => ({ ...m, body: editor.getData() }))} />
              </Box>
              {errors.body && <Typography variant="caption" sx={{ color: "red" }}>{errors.body}</Typography>}
              <Typography variant="caption" sx={{ display: "block", color: "#666", mt: 0.5 }}>
                Images: use the image button and paste an https:// link that anyone can open (e.g. on the CET Cell website). Keep images small (under 200 KB) and give each one alternative text.
              </Typography>
              <FormControlLabel sx={{ mt: 1 }} control={<Checkbox size="small" checked={ownText} onChange={(e) => setOwnText(e.target.checked)} />} label={<Typography variant="body2">Write my own plain-text version (otherwise made from the body)</Typography>} />
              {ownText && <TextField fullWidth multiline minRows={4} size="small" value={email.textbody} onChange={(e) => setEmail({ ...email, textbody: e.target.value })} />}
            </Box>
          </Grid>
        )}

        {serverError && (
          <Grid item xs={12}>
            <Alert severity="error">{serverError}</Alert>
          </Grid>
        )}
        <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => navigate(`/${project}/notification-templates?page=list`)}>Back</Button>
          <Button variant="outlined" size="small" onClick={() => { if (validate()) setPreview(true); }}>Preview</Button>
          <Button variant="contained" size="small" disabled={saving} onClick={save} sx={{ backgroundColor: "#e697ab", color: cssProperties?.surface?.primary, "&:hover": { backgroundColor: "#d9849a" } }}>
            {saving ? "Saving..." : isEdit ? "Save" : "Create"}
          </Button>
        </Grid>
      </Grid>
      {preview && <PreviewDialog unsaved template={payload()} systemVariables={systemVariables} onClose={() => setPreview(false)} />}
    </Box>
  );
}
